// E1 — turnRouter: o "proxy.ts" de um TURNO de LLM. Dado o texto do turno (e se o
// turno anterior tocou código), classifica o PAPEL (analysis|build|risky|chat) e
// devolve a decisão de roteamento {readOnly, permissionsMode, modelTier} a partir da
// tabela de policy. É a peça que casa Cursor/Codex "Ask vs Agent": determinística,
// barata (sem chamada de LLM), conservadora.
//
// PURO + Dependency Rule: importa SOMENTE o domínio (`./turnSignals.js` +
// `./routePolicy.js`) — nunca React, Electron ou o modelRegistry. O modelo concreto
// (resolver 'cheap' → um id) é INFRA e mora no resolver/wiring, não aqui: routeTurn
// devolve só o `modelTier`. (Travado pelo teste arch:pure-domain.)

import { detectTurnSignals } from './turnSignals.js';
import { ROLES, policyForRole } from './routePolicy.js';

// Precedência CONSERVADORA: risky > build > analysis > chat. Um turno que mostra
// QUALQUER pista de build/risky NUNCA vira somente-leitura — o pior caso de um
// falso-positivo de análise seria bloquear uma escrita, então build/risky vencem.
function classifyRole(sig, priorTurnTouchedCode) {
  if (sig.risky) return ROLES.RISKY;
  if (sig.build) return ROLES.BUILD;
  if (sig.readOnly) return ROLES.ANALYSIS;
  // priorTurnTouchedCode suprime 'chat': um "ok, continua" logo após uma escrita é
  // continuação de build, não conversa — roteá-lo somente-leitura travaria o follow-up.
  if (sig.chat && !priorTurnTouchedCode) return ROLES.CHAT;
  return ROLES.NONE;
}

const ROLE_LABEL = {
  [ROLES.ANALYSIS]: 'análise',
  [ROLES.BUILD]: 'build',
  [ROLES.RISKY]: 'arriscado',
  [ROLES.CHAT]: 'conversa',
  [ROLES.NONE]: 'neutro',
};

const ROLE_EFFECT = {
  [ROLES.ANALYSIS]: 'modelo mais barato + somente leitura',
  [ROLES.CHAT]: 'modelo mais barato + somente leitura',
  [ROLES.BUILD]: 'modelo e ferramentas mantidos',
  [ROLES.RISKY]: 'pede confirmação (ask) + ferramentas completas',
  [ROLES.NONE]: 'sem roteamento',
};

// reason: explicação curta em PT-BR p/ o indicador (transparência, igual o Cursor
// mostra o modo). Limita as pistas pra não virar parede de texto.
function buildReason(role, evidence) {
  if (role === ROLES.NONE) return 'sem sinal claro; turno não roteado';
  const cues = Array.isArray(evidence) ? evidence.filter(Boolean).slice(0, 3) : [];
  const head = `${ROLE_LABEL[role]} → ${ROLE_EFFECT[role]}`;
  return cues.length ? `${head} (${cues.join(', ')})` : head;
}

/**
 * routeTurn(ctx) → { role, reason, readOnly, permissionsMode, modelTier }
 * ctx = { text:string, priorTurnTouchedCode?:boolean }.
 * Determinística, nunca lança. role 'none' ⇒ policy no-op (sem mudança de comportamento).
 * Devolve modelTier (NÃO um id concreto) — o resolver injeta o registry no wiring.
 */
export function routeTurn(ctx) {
  const sig = detectTurnSignals(ctx); // nunca lança; não-string → tudo false
  const priorTurnTouchedCode = !!(ctx && typeof ctx === 'object' && ctx.priorTurnTouchedCode);
  const role = classifyRole(sig, priorTurnTouchedCode);
  const policy = policyForRole(role);
  return {
    role,
    reason: buildReason(role, sig.evidence),
    readOnly: policy.readOnly,
    permissionsMode: policy.permissionsMode,
    modelTier: policy.modelTier,
  };
}
